import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-[var(--opacity-disabled,0.4)] [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:outline-2 focus-visible:outline-[var(--color-accent-primary,var(--color-accent-gold))] focus-visible:outline-offset-2",
  {
    variants: {
      variant: {
        default:
          "bg-[var(--btn-primary-bg)] text-[var(--btn-primary-text)] hover:bg-[var(--btn-primary-hover-bg)] active:bg-[var(--btn-primary-active-bg)]",
        destructive: "bg-destructive text-white hover:bg-destructive/90",
        outline:
          "border border-[var(--btn-outline-border)] text-[var(--btn-outline-text)] hover:bg-[var(--btn-outline-hover-bg)] hover:text-[var(--btn-outline-hover-text)]",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost:
          "text-[var(--btn-ghost-text)] hover:bg-[var(--btn-ghost-hover-bg)] hover:text-[var(--btn-ghost-hover-text)]",
        link: "text-[var(--color-accent-primary,var(--color-accent-gold))] underline-offset-4 hover:underline",
      },
      size: {
        default: "h-[var(--btn-md-height)] px-4 py-2 text-[var(--btn-md-font-size)]",
        sm: "h-[var(--btn-sm-height)] rounded-md gap-1.5 px-3 text-[var(--btn-sm-font-size)]",
        lg: "h-[var(--btn-lg-height)] rounded-md px-6 text-[var(--btn-lg-font-size)]",
        icon: "size-[var(--btn-md-height)]",
        "icon-sm": "size-[var(--btn-sm-height)]",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : "button"

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
